import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  buildBrandCatalogProfile,
  type BrandCatalogProfile,
} from '../utils/brandCatalogProfile';

function profileRows(profile: BrandCatalogProfile): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Sells', value: profile.sellsLabel },
    { label: 'SKU', value: profile.skuLooksLike },
  ];
  if (profile.skuExample) rows.push({ label: 'Example', value: profile.skuExample });
  if (profile.mpnRule) rows.push({ label: 'MPN', value: profile.mpnRule });
  rows.push(
    { label: 'Barcodes', value: `${profile.barcodeModelLabel} — ${profile.barcodeModelExplain}` },
    { label: 'UPC', value: profile.upcLooksLike },
    { label: 'EAN', value: profile.eanLooksLike },
    { label: 'GTIN', value: profile.gtinLooksLike },
  );
  if (profile.gs1Notes) rows.push({ label: 'GS1', value: profile.gs1Notes });
  if (profile.scraperHint) rows.push({ label: 'Ingest', value: profile.scraperHint });
  if (profile.source) rows.push({ label: 'Source', value: profile.source });
  return rows;
}

export const BrandCatalogProfileBody: React.FC<{
  profile: BrandCatalogProfile;
  compact?: boolean;
}> = ({ profile, compact }) => {
  const rows = profileRows(profile);
  return (
    <Box>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Chip
          size="small"
          label={profile.barcodeModelLabel}
          color={profile.barcodeModel === 'unknown' ? 'default' : 'info'}
          variant="outlined"
        />
        <Chip size="small" label={profile.confidenceLabel} variant="outlined" />
      </Stack>
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: compact ? '72px 1fr' : '88px 1fr' },
            gap: 1.25,
            py: compact ? 0.7 : 0.85,
            borderTop: '1px solid',
            borderColor: 'divider',
            '&:first-of-type': { borderTop: 'none' },
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.15 }}>
            {row.label}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: compact ? '0.78rem' : '0.84rem', lineHeight: 1.45, minWidth: 0 }}>
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const BrandCatalogProfileDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  profile: BrandCatalogProfile;
}> = ({ open, onClose, profile }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pr: 6 }}>
      {profile.name} catalog rules
      <IconButton
        aria-label="Close brand rules"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <BrandCatalogProfileBody profile={profile} />
    </DialogContent>
  </Dialog>
);

export const BrandCatalogProfileButton: React.FC<{
  brandId?: string | null;
  brand?: string | null;
  sells?: string[] | null;
}> = ({ brandId, brand, sells }) => {
  const [open, setOpen] = useState(false);
  const profile = buildBrandCatalogProfile(brandId, brand, sells);
  if (!profile) return null;
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
      >
        Brand rules
      </Button>
      <BrandCatalogProfileDialog open={open} onClose={() => setOpen(false)} profile={profile} />
    </>
  );
};
